import { useMutation } from "@tanstack/react-query";
import { parseReEngageError } from "@/lib/reEngageErrors";
import { reEngagePost } from "@/lib/api/reEngageClient";

/**
 * Shared mutation-meta marker for Re-Engage mutations. Imported by both the
 * hook(s) that opt into the AUTH_EXPIRED invalidation (writers) and the
 * MutationCache.onError handler in providers.tsx (reader). Single source of
 * truth: a typo on either side becomes a TypeScript error rather than
 * silently disabling cache invalidation.
 *
 * Bryan review #3: previously this was a string literal duplicated at writer
 * (`meta: { feature: "reEngage" }`) and reader (`meta?.feature === "reEngage"`)
 * sites with no compile-time link. A typo (`features:`, `"ReEngage"`,
 * `"re-engage"`) would silently break invalidation with no signal.
 */
export const REENGAGE_MUTATION_META = { feature: "reEngage" } as const;

export interface StartReEngageInput {
  campaignId: string;
  channelId: string;
  cohortFilter: string[];
  voiceAgentId: string;
  queueSize: number;
  parallelLines: number;
  channelSkips: {
    multiLineDialer: boolean;
    smsBlast: boolean;
    ringlessVm: boolean;
  };
  /**
   * Plan v6 Decision 5 idempotency nonce. Sent as `Idempotency-Key` header
   * (NOT body). Wizard mints fresh per submit, reuses on transport retry.
   */
  idempotencyKey?: string;
}

export interface StartReEngageResult {
  runId: string;
  /**
   * `true` when every property in the user's filter was blocked by hard-blocks
   * (DNC / phone-health / freq-cap / attempt-cap / dial-window / no-phone) and
   * the run completed instantly without dialing. Backend computes this by
   * checking whether `cohortPropertyIds.length === 0` after applying
   * pre-dial filters. The wizard surfaces this with a distinct toast
   * ("No properties matched your filter") instead of the generic success
   * toast, so the rep knows to broaden their selection rather than thinking
   * the wizard froze. Source: `backend/src/routes/dialerReEngage.mjs` POST handler.
   */
  emptyCohort?: boolean;
  /**
   * Server-derived flag (plan v6 Decision 9). `true` if the submitted
   * `parallelLines` equals the user's plan ceiling. Wizard reports this to
   * PostHog as authoritative — client-side derivation from `usePlanLimits`
   * would lie inside the 30s staleTime window if the rep upgraded mid-modal.
   */
  parallelLinesAtPlanMax?: boolean;
}

/**
 * Submits a new Re-Engage run.
 *
 * Throws a ReEngageApiError on non-OK responses. Consumers (the wizard's
 * `handleSubmit`) dispatch on `err.code` via the `classifySubmitError` helper:
 *
 *   - `RATE_LIMITED`              — 429; envelope carries `retryAfter` (seconds)
 *   - `COHORT_TOO_LARGE`          — 413; cohort > 5,000 properties
 *   - `RE_ENGAGE_DISABLED`        — 503; feature is off for this account
 *   - `RE_ENGAGE_RUN_ALREADY_ACTIVE` — 409; another rep started a run
 *
 * The previous string-prefix protocol (`RATE_LIMITED:<seconds>` / `COHORT_TOO_LARGE`)
 * is gone — `parseReEngageError` reads the typed envelope on the backend.
 */
export function useStartReEngageRun() {
  return useMutation<StartReEngageResult, Error, StartReEngageInput>({
    // Scopes the global MutationCache.onError handler in providers.tsx:
    // when this mutation throws AUTH_EXPIRED, the handler invalidates
    // ["reEngage", "featureFlag"] so the page button re-derives state.
    // The meta object is the shared constant above so writer and reader
    // are compile-time linked.
    meta: REENGAGE_MUTATION_META,
    mutationFn: async (input: StartReEngageInput) => {
      // Strip idempotencyKey from the body — it travels in the header.
      const { idempotencyKey, ...body } = input;
      const res = await reEngagePost(undefined, body, { idempotencyKey });
      const err = await parseReEngageError(res);
      if (err) throw err;
      return (await res.json()) as StartReEngageResult;
    },
  });
}
